import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Spinner } from '@components/core-ui/spinners/Spinner';
import EmailIcon from '@components/icons/Email.svg';
import EyeIcon from '@components/icons/Eye.svg';
import EyeClosedIcon from '@components/icons/EyeClosed.svg';
import { cn } from '@helpers/cn';
import { normalizeEmail } from '@helpers/normalizeEmail';
import { redirectBrowserTo } from '@helpers/redirectBrowserTo';
import { useAPI } from '@providers/useAPI';
import { useQueryParams } from '@providers/useQueryParams';
import { routes } from '@routes';
import { useMutation } from '@tanstack/react-query';
import type { CheckDomainBodyDto } from 'app/API';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createSearchParams, Link } from 'react-router-dom';
import { createSsoLoginRedirectUrl } from '../../../SSOLogin/helpers/createSsoLoginRedirectUrl';
import { getSafeSsoLogoutUrl } from '../../../SSOLogin/helpers/getSafeSsoLogoutUrl';
import { checkDomain } from '../../api/checkDomain';
import { usePageContext } from '../../providers/PageProvider';
import type { TDesktopSignIn } from '../../types';
import { LoginButton } from './components/LoginButton';
import { isValidEmail } from './helpers/isValidEmail';
import { TLoginStep } from './types/TLoginStep';

type TProps = {
  desktop?: TDesktopSignIn;
};

type TCheckDomainVariables = {
  requestBody: CheckDomainBodyDto;
  signal: AbortSignal;
};

export const LoginPageContent = ({ desktop }: TProps) => {
  const { call } = useAPI();
  const { clearError, isLoading, email, setEmail, password, setPassword, error, login } = usePageContext();
  const {
    isPending: isCheckingDomain,
    mutateAsync: checkSignInMethod,
    reset: resetDomainCheck,
  } = useMutation({
    mutationFn: ({ requestBody, signal }: TCheckDomainVariables) => checkDomain({ call, requestBody, signal }),
  });
  const { mutateAsync: startSso } = useMutation({
    mutationFn: ({ requestBody, signal }: TCheckDomainVariables) => checkDomain({ call, requestBody, signal }),
  });
  const queryParams = useQueryParams();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const [emailError, setEmailError] = useState(false);
  const [domainCheckError, setDomainCheckError] = useState('');
  const [isRedirectingToSso, setIsRedirectingToSso] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loginStep, setLoginStep] = useState<TLoginStep>('CHECK_DOMAIN');
  const [ssoRedirectUrl, setSsoRedirectUrl] = useState<string | null>(null);
  const hasAutoCheckedDomain = useRef(false);

  const abortActiveRequest = useCallback(() => {
    activeRequest.current?.abort();
    activeRequest.current = null;
  }, []);

  const startRequest = useCallback(() => {
    abortActiveRequest();
    const controller = new AbortController();
    activeRequest.current = controller;
    return controller.signal;
  }, [abortActiveRequest]);

  useEffect(() => abortActiveRequest, [abortActiveRequest]);

  useEffect(() => {
    const handlePageShow = (evt: PageTransitionEvent) => {
      if (evt.persisted) {
        setIsRedirectingToSso(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (loginStep === 'PASSWORD_LOGIN') {
      passwordInputRef.current?.focus();
    }
  }, [loginStep]);

  const handleEmailDomainChecking = useCallback(async () => {
    const normalizedEmail = normalizeEmail(email);

    if (!isValidEmail(normalizedEmail)) {
      setEmailError(true);
      setDomainCheckError('Please enter a valid email address.');
      emailInputRef.current?.focus();
      return;
    }

    const signal = startRequest();
    setEmail(normalizedEmail);
    setEmailError(false);
    setDomainCheckError('');

    try {
      const result = await checkSignInMethod({
        requestBody: { domain: normalizedEmail, email: normalizedEmail },
        signal,
      });

      if (signal.aborted) {
        return;
      }

      const redirectUrl = result?.ssoRedirectUrl;
      const logoutUrl = result?.ssoLogoutUrl;
      if (redirectUrl !== null && typeof redirectUrl !== 'string') {
        throw new Error('Invalid domain check response');
      }
      if (logoutUrl !== undefined && logoutUrl !== null && typeof logoutUrl !== 'string') {
        throw new Error('Invalid domain check response');
      }

      setSsoRedirectUrl(redirectUrl);
      setLoginStep(redirectUrl ? 'SSO_LOGIN' : 'PASSWORD_LOGIN');
    } catch {
      if (signal.aborted) {
        return;
      }

      setSsoRedirectUrl(null);
      setLoginStep('CHECK_DOMAIN');
      setDomainCheckError("We couldn't check your sign-in method. Please try again.");
    }
  }, [checkSignInMethod, email, setEmail, startRequest]);

  useEffect(() => {
    if (!desktop?.email || hasAutoCheckedDomain.current || !email) {
      return;
    }

    hasAutoCheckedDomain.current = true;
    void handleEmailDomainChecking();
  }, [desktop?.email, email, handleEmailDomainChecking]);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((isVisible) => !isVisible);
  };

  const isEmailLocked = loginStep !== 'CHECK_DOMAIN';

  const resetToDomainCheck = () => {
    abortActiveRequest();
    resetDomainCheck();
    setPassword('');
    clearError();
    setEmailError(false);
    setDomainCheckError('');
    setIsRedirectingToSso(false);
    setIsPasswordVisible(false);
    setLoginStep('CHECK_DOMAIN');
    setSsoRedirectUrl(null);
  };

  const handleEmailChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const nextEmail = evt.target.value;

    if (isEmailLocked || nextEmail === email) {
      return;
    }

    setEmail(nextEmail);
    resetToDomainCheck();
  };

  const handleChangeEmail = () => {
    resetToDomainCheck();
    emailInputRef.current?.focus();
  };

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    if (loginStep === 'PASSWORD_LOGIN') {
      void login();
      return;
    }

    if (loginStep === 'SSO_LOGIN' && ssoRedirectUrl) {
      const normalizedEmail = normalizeEmail(email);
      const signal = startRequest();
      setDomainCheckError('');
      setIsRedirectingToSso(true);

      try {
        const requestBody = {
          consumeSsoSessionMarker: !desktop,
          domain: normalizedEmail,
          email: normalizedEmail,
        };
        const result = await startSso({ requestBody, signal });

        if (signal.aborted) {
          return;
        }

        const freshRedirectUrl = result?.ssoRedirectUrl;
        const rawLogoutUrl = result?.ssoLogoutUrl;
        const freshLogoutUrl = getSafeSsoLogoutUrl(rawLogoutUrl);
        const shouldLogout = result?.shouldLogoutPreviousSsoSession;

        if (
          (freshRedirectUrl !== null && typeof freshRedirectUrl !== 'string') ||
          (rawLogoutUrl !== null && typeof rawLogoutUrl !== 'string') ||
          typeof shouldLogout !== 'boolean'
        ) {
          throw new Error('Invalid domain check response');
        }

        if (shouldLogout && !desktop) {
          if (!freshLogoutUrl) {
            throw new Error('Invalid domain check response');
          }

          redirectBrowserTo(freshLogoutUrl);
          return;
        }

        if (!freshRedirectUrl) {
          throw new Error('Invalid domain check response');
        }

        redirectBrowserTo(
          createSsoLoginRedirectUrl(freshRedirectUrl, desktop?.returnUrl ?? queryParams.get('goto'), freshLogoutUrl),
        );
      } catch {
        if (!signal.aborted) {
          setIsRedirectingToSso(false);
          setDomainCheckError("We couldn't start your company login. Please try again.");
        }
      }
      return;
    }

    void handleEmailDomainChecking();
  };

  const goto = queryParams.get('goto');
  const currentEmail = normalizeEmail(email);
  const signupSearch = createSearchParams({
    ...(goto ? { goto } : {}),
    ...(currentEmail ? { email: currentEmail } : {}),
  }).toString();
  const recoverySearch = createSearchParams(currentEmail ? { email: currentEmail } : {}).toString();
  const signupUrl = signupSearch ? `${routes.signup.make()}?${signupSearch}` : routes.signup.make();
  const recoveryUrl = recoverySearch
    ? `${routes.passwordRecovery.make()}?${recoverySearch}`
    : routes.passwordRecovery.make();
  const isButtonDisabled =
    isCheckingDomain ||
    isRedirectingToSso ||
    isLoading ||
    !currentEmail ||
    (loginStep === 'PASSWORD_LOGIN' && !password) ||
    (loginStep === 'SSO_LOGIN' && !ssoRedirectUrl);
  const visibleError = domainCheckError || error;

  return (
    <>
      {isLoading && <Spinner overlay={true} />}
      <div className="w-full max-w-sm pb-[100px] pt-5 text-center">
        <h1 className="mb-8 text-[42px] font-semibold leading-[1.3] text-title">Welcome!</h1>
        <h2 className="mb-[50px] text-[17px] font-semibold leading-[1.3] text-title">Sign In to Your Account</h2>

        <form className="text-left" noValidate onSubmit={handleSubmit}>
          <div
            className={cn('[&_input::placeholder]:normal-case [&_input]:lowercase', isEmailLocked ? 'mb-1.5' : 'mb-8')}
          >
            <Input
              ref={emailInputRef}
              autoCapitalize="none"
              autoComplete="email"
              spellCheck={false}
              onChange={handleEmailChange}
              name="email"
              label="Email"
              type="email"
              value={email || ''}
              required={true}
              error={emailError}
              icon={EmailIcon}
              disabled={isLoading}
              readOnly={isEmailLocked}
            />
          </div>

          {isEmailLocked ? (
            <div className="mb-8 flex justify-end">
              <TextButton
                onClick={handleChangeEmail}
                className="-mr-2.5 h-auto"
                disabled={isLoading || isRedirectingToSso}
              >
                Switch account
              </TextButton>
            </div>
          ) : null}

          {loginStep === 'PASSWORD_LOGIN' ? (
            <>
              <div className="mb-8">
                <Input
                  ref={passwordInputRef}
                  autoComplete="current-password"
                  onChange={(evt) => {
                    setPassword(evt.target.value);
                    clearError();
                  }}
                  onIconClick={togglePasswordVisibility}
                  iconButtonLabel={isPasswordVisible ? 'Hide password' : 'Show password'}
                  name="password"
                  label="Password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={password || ''}
                  required={true}
                  icon={isPasswordVisible ? EyeIcon : EyeClosedIcon}
                  disabled={isLoading}
                />
              </div>
              <Link
                to={recoveryUrl}
                className="mb-8 block text-center text-xs font-medium leading-none text-foreground-muted underline hover:no-underline"
              >
                Forgot password?
              </Link>
            </>
          ) : null}

          {visibleError ? (
            <div className="mb-8" data-testid="errorMessage" role="alert">
              <ErrorMessage error={visibleError} />
            </div>
          ) : null}

          <LoginButton
            disabled={isButtonDisabled}
            isCheckingDomain={isCheckingDomain}
            isRedirectingToSso={isRedirectingToSso}
            loginStep={loginStep}
          />

          {desktop ? null : (
            <p className="mb-8 text-center text-xs font-medium leading-none">
              {"Don't have an account yet? "}&nbsp;&nbsp;
              <Link
                to={signupUrl}
                aria-disabled={isRedirectingToSso}
                tabIndex={isRedirectingToSso ? -1 : undefined}
                onClick={(evt) => {
                  if (isRedirectingToSso) {
                    evt.preventDefault();
                  }
                }}
                className={cn('text-link underline hover:no-underline', isRedirectingToSso && 'pointer-events-none')}
              >
                Sign Up
              </Link>
            </p>
          )}
        </form>
      </div>
    </>
  );
};
